import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Package } from "lucide-react";
import { toast } from "sonner";

const KEY = "pkg_compare";
const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };

// Shared shortlist hook — survives refresh (device-local), synced live across tabs/screens.
export function useCompare() {
  const [compare, setCompare] = useState(read);
  useEffect(() => {
    const on = () => setCompare(read());
    window.addEventListener("pkg-compare-changed", on);
    window.addEventListener("storage", on);
    return () => { window.removeEventListener("pkg-compare-changed", on); window.removeEventListener("storage", on); };
  }, []);
  const persist = (next) => { localStorage.setItem(KEY, JSON.stringify(next)); window.dispatchEvent(new Event("pkg-compare-changed")); return next; };
  const inCompare = (id) => compare.some((c) => c.service_id === id);
  const toggle = (s) => {
    const cur = read();
    let next;
    if (cur.some((c) => c.service_id === s.service_id)) next = cur.filter((c) => c.service_id !== s.service_id);
    else { if (cur.length >= 3) { toast.error("You can compare up to 3 packages"); return; } next = [...cur, { service_id: s.service_id, slug: s.slug, name: s.name, images: s.images, price: s.price, pricing_mode: s.pricing_mode, holiday: s.holiday, verified_seller: s.verified_seller }]; }
    setCompare(persist(next));
  };
  const clear = () => setCompare(persist([]));
  return { compare, inCompare, toggle, clear };
}

export function CompareBar({ compare, onOpen, onClear }) {
  if (!compare.length) return null;
  return (
    <div className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full bg-slate-900 px-4 py-2.5 text-white shadow-xl" data-testid="package-compare-bar">
      <span className="text-sm font-semibold">{compare.length} package{compare.length > 1 ? "s" : ""} to compare</span>
      <button onClick={onOpen} disabled={compare.length < 2} data-testid="package-compare-open" className="rounded-full bg-fuchsia-600 px-3 py-1.5 text-xs font-bold disabled:opacity-40">Compare</button>
      <button onClick={onClear} data-testid="package-compare-clear" className="text-xs font-medium text-white/70 hover:text-white">Clear</button>
    </div>
  );
}

export function PkgCompare({ items, onClose, onRemove }) {
  const BE = process.env.REACT_APP_BACKEND_URL || "";
  const src = (u) => (u && u.startsWith("/api") ? BE + u : u);
  const priceText = (s) => {
    const hh = s.holiday || {};
    if (s.pricing_mode === "quote") return "On request";
    if (s.pricing_mode === "negotiable") return "Negotiable";
    return `${s.pricing_mode === "starting_from" ? "From " : ""}₹${s.price || Number(hh.pricing?.adult) || 0}`;
  };
  const rows = [
    ["Duration", (s) => { const h = s.holiday || {}; return (h.days || h.nights) ? `${h.days || "?"}D / ${h.nights || "?"}N` : "—"; }],
    ["Price", priceText],
    ["Destinations", (s) => ((s.holiday || {}).stops || []).join(", ") || (s.holiday || {}).start_location || "—"],
    ["Meal plan", (s) => (s.holiday || {}).meal_plan || "—"],
    ["Hotels", (s) => (((s.holiday || {}).accommodation || {}).hotels || []).map((h) => `${h.name}${h.tier ? ` (${h.tier})` : ""}`).join(", ") || "—"],
    ["Themes", (s) => ((s.holiday || {}).themes || []).join(", ") || "—"],
    ["Itinerary", (s) => `${((s.holiday || {}).itinerary || []).length} days planned`],
    ["Inclusions", (s) => `${((s.holiday || {}).inclusions || []).length} listed`],
    ["Verified agent", (s) => (s.verified_seller ? "✓ Yes" : "—")],
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="package-compare-modal">
      <div className="max-h-[88vh] w-full max-w-4xl overflow-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800">Compare packages</h3>
          <button onClick={onClose} data-testid="package-compare-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="w-32"></th>
                {items.map((s) => (
                  <th key={s.service_id} className="min-w-[180px] p-2 align-top">
                    <div className="overflow-hidden rounded-xl border border-slate-200">
                      <div className="h-24 w-full bg-slate-100">{(s.images || [])[0] ? <img src={src(s.images[0])} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center bg-gradient-to-br from-fuchsia-500 to-sky-500 text-white"><Package className="h-6 w-6" /></div>}</div>
                      <div className="p-2">
                        <p className="line-clamp-2 text-left text-xs font-bold text-slate-800">{s.name}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <Link to={`/service/${s.slug}`} className="text-[10px] font-semibold text-fuchsia-600 hover:underline">View</Link>
                          <button onClick={() => onRemove(s)} data-testid={`package-compare-remove-${s.service_id}`} className="text-[10px] font-semibold text-slate-400 hover:text-rose-500">Remove</button>
                        </div>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, fn]) => (
                <tr key={label} className="border-t border-slate-100">
                  <td className="p-2 text-xs font-semibold text-slate-500">{label}</td>
                  {items.map((s) => <td key={s.service_id} className="p-2 align-top text-xs text-slate-700">{fn(s)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
